import { useState, useEffect, useCallback, useMemo } from "react";
import {
  FiChevronLeft, FiChevronRight, FiCalendar, FiClock, FiUser, FiMail, FiVideo, FiX, FiFilter
} from "react-icons/fi";
import { getBookings, getMeetingTypes } from "../../api/meetingsApi";
import toast from "react-hot-toast";

const EVENT_COLORS = [
  { bg: "bg-pink-100/80", text: "text-pink-900", border: "border-pink-300", accent: "bg-pink-500" },
  { bg: "bg-blue-100/80", text: "text-blue-900", border: "border-blue-300", accent: "bg-blue-500" },
  { bg: "bg-emerald-100/80", text: "text-emerald-900", border: "border-emerald-300", accent: "bg-emerald-500" },
  { bg: "bg-amber-100/80", text: "text-amber-900", border: "border-amber-300", accent: "bg-amber-500" },
  { bg: "bg-purple-100/80", text: "text-purple-900", border: "border-purple-300", accent: "bg-purple-500" },
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

function formatHour(h) {
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(hour12).padStart(2, "0")} ${period}`;
}

export default function CalendarPage() {
  const [viewMode, setViewMode] = useState("week"); // 'day' | 'week' | 'month'
  const [currentDate, setCurrentDate] = useState(new Date());

  const [bookings, setBookings] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const [selectedEvent, setSelectedEvent] = useState(null);

  // Load bookings and meeting types
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [bData, tData] = await Promise.all([
        getBookings("confirmed"),
        getMeetingTypes(),
      ]);
      const bList = Array.isArray(bData) ? bData : [];
      const tList = Array.isArray(tData) ? tData : [];

      setBookings(bList);
      setMeetingTypes(tList);
      setSelectedTypes(new Set(tList.map(t => t.id)));
    } catch {
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Map meeting type IDs to color themes deterministically
  const typeColorMap = useMemo(() => {
    const map = {};
    meetingTypes.forEach((t, i) => {
      map[t.id] = EVENT_COLORS[i % EVENT_COLORS.length];
    });
    return map;
  }, [meetingTypes]);

  const toggleTypeFilter = (id) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Compute start/end of current week (Monday to Sunday)
  const weekDays = useMemo(() => {
    const curr = new Date(currentDate);
    const day = curr.getDay();
    const diffToMon = curr.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(curr.setDate(diffToMon));

    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      days.push(d);
    }
    return days;
  }, [currentDate]);

  // Navigate dates
  const navigateDate = (direction) => {
    const next = new Date(currentDate);
    if (viewMode === "day") {
      next.setDate(next.getDate() + direction);
    } else if (viewMode === "week") {
      next.setDate(next.getDate() + direction * 7);
    } else {
      next.setMonth(next.getMonth() + direction);
    }
    setCurrentDate(next);
  };

  // Filtered bookings
  const filteredBookings = useMemo(() => {
    return bookings.filter(b => selectedTypes.has(b.meeting_type));
  }, [bookings, selectedTypes]);

  // Check if booking falls on specific date and hour
  const getBookingsForSlot = (dateObj, hour) => {
    const dateStr = dateObj.toISOString().split("T")[0];
    return filteredBookings.filter(b => {
      const dt = new Date(b.scheduled_at);
      const bDateStr = dt.toISOString().split("T")[0];
      const bHour = dt.getHours();
      return bDateStr === dateStr && bHour === hour;
    });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 font-sans">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-slate-800">Calendar</h1>
          <div className="h-6 w-px bg-slate-200 hidden sm:block" />

          {/* Month Title & Nav */}
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-slate-700">
              {MONTHS[currentDate.getMonth()]}, {currentDate.getFullYear()}
            </span>
            {loading && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-r-transparent ml-1" title="Loading..." />
            )}
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl ml-2">
              <button
                onClick={() => navigateDate(-1)}
                className="p-1.5 hover:bg-white rounded-lg text-slate-600 transition shadow-sm"
              >
                <FiChevronLeft size={16} />
              </button>
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-2.5 py-1 text-xs font-bold hover:bg-white rounded-lg text-slate-700 transition"
              >
                Today
              </button>
              <button
                onClick={() => navigateDate(1)}
                className="p-1.5 hover:bg-white rounded-lg text-slate-600 transition shadow-sm"
              >
                <FiChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* View Mode Switcher */}
        <div className="flex items-center bg-slate-100 p-1 rounded-xl">
          {["day", "week"].map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-1.5 text-xs font-bold rounded-lg capitalize transition-all ${
                viewMode === mode ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      {/* Main Calendar Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* LEFT SIDEBAR (Mini Calendar & Schedule Filters) */}
        <div className="lg:col-span-3 space-y-6">

          {/* Mini Calendar Widget */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-800">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => navigateDate(-1)} className="p-1 text-slate-400 hover:text-slate-700">
                  <FiChevronLeft size={14} />
                </button>
                <button onClick={() => navigateDate(1)} className="p-1 text-slate-400 hover:text-slate-700">
                  <FiChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Mini Grid */}
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold text-slate-400">
              {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map(d => <span key={d}>{d}</span>)}
            </div>

            <div className="grid grid-cols-7 gap-1 text-center">
              {weekDays.map((d, i) => {
                const isSelected = d.toDateString() === currentDate.toDateString();
                const isToday = d.toDateString() === new Date().toDateString();
                return (
                  <button
                    key={i}
                    onClick={() => setCurrentDate(d)}
                    className={`h-7 w-7 mx-auto rounded-full text-xs font-bold flex items-center justify-center transition ${
                      isSelected
                        ? "bg-blue-600 text-white shadow-sm"
                        : isToday
                        ? "bg-blue-50 text-blue-600 border border-blue-200"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Schedule Filters */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <FiFilter size={12} /> Filter Meeting Types
            </h3>

            {meetingTypes.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No meeting types configured</p>
            ) : (
              <div className="space-y-2">
                {meetingTypes.map(type => {
                  const color = typeColorMap[type.id] || EVENT_COLORS[0];
                  const isChecked = selectedTypes.has(type.id);
                  return (
                    <label
                      key={type.id}
                      className="flex items-center gap-2.5 text-xs font-semibold text-slate-700 cursor-pointer p-1.5 rounded-lg hover:bg-slate-50 transition"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleTypeFilter(type.id)}
                        className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                      />
                      <span className={`w-2.5 h-2.5 rounded-full ${color.accent}`} />
                      <span className="truncate">{type.title}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SCHEDULE GRID */}
        <div className="lg:col-span-9 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">

          {/* Days Header */}
          <div className={`grid border-b border-slate-200 bg-slate-50/70 text-slate-700 font-bold text-xs ${
            viewMode === "day" ? "grid-cols-2" : "grid-cols-8"
          }`}>
            <div className="p-3 border-r border-slate-200 text-center text-slate-400">Time</div>
            {(viewMode === "day" ? [currentDate] : weekDays).map((d, i) => {
              const isToday = d.toDateString() === new Date().toDateString();
              return (
                <div key={i} className="p-3 text-center border-r border-slate-200 last:border-r-0">
                  <span className="text-slate-400 block text-[10px] uppercase font-semibold">{DAYS_SHORT[d.getDay()]}</span>
                  <span className={`inline-block text-sm mt-0.5 ${
                    isToday ? "bg-blue-600 text-white px-2 py-0.5 rounded-full font-bold" : ""
                  }`}>
                    {d.getDate()}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Hourly Slots Grid */}
          <div className="divide-y divide-slate-100 overflow-y-auto max-h-[600px]">
            {HOURS.map(hour => (
              <div key={hour} className={`grid min-h-[64px] ${viewMode === "day" ? "grid-cols-2" : "grid-cols-8"}`}>
                {/* Time Label */}
                <div className="p-2 border-r border-slate-200 text-[11px] font-semibold text-slate-400 text-center flex items-center justify-center bg-slate-50/40">
                  {formatHour(hour)}
                </div>

                {/* Day Columns */}
                {(viewMode === "day" ? [currentDate] : weekDays).map((dateObj, colIdx) => {
                  const slotBookings = getBookingsForSlot(dateObj, hour);

                  return (
                    <div
                      key={colIdx}
                      className="p-1 border-r border-slate-100 last:border-r-0 relative hover:bg-slate-50/50 transition flex flex-col gap-1"
                    >
                      {slotBookings.map(b => {
                        const theme = typeColorMap[b.meeting_type] || EVENT_COLORS[0];
                        const dt = new Date(b.scheduled_at);
                        const timeStr = dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

                        return (
                          <button
                            key={b.id}
                            onClick={() => setSelectedEvent(b)}
                            className={`${theme.bg} ${theme.text} ${theme.border} border p-2 rounded-xl text-left shadow-sm hover:shadow transition-all w-full space-y-1`}
                          >
                            <p className="font-bold text-xs truncate leading-tight">{b.client_name}</p>
                            <div className="flex items-center gap-1 text-[10px] font-medium opacity-80">
                              <FiClock size={10} />
                              <span>{timeStr} · {b.meeting_type_duration}m</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

        </div>

      </div>

      {/* EVENT DETAILS MODAL */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 max-w-md w-full p-6 space-y-5 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                  <FiCalendar size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-base">{selectedEvent.meeting_type_title}</h3>
                  <p className="text-xs text-slate-400">Booking Details</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedEvent(null)}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg transition"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="space-y-3 bg-slate-50 p-4 rounded-2xl border border-slate-100 text-xs">
              <div className="flex items-center gap-3">
                <FiUser className="text-blue-600 shrink-0" size={15} />
                <div>
                  <p className="text-slate-400 font-medium">Client Name</p>
                  <p className="font-bold text-slate-800">{selectedEvent.client_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FiMail className="text-blue-600 shrink-0" size={15} />
                <div>
                  <p className="text-slate-400 font-medium">Client Email</p>
                  <p className="font-bold text-slate-800">{selectedEvent.client_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FiClock className="text-blue-600 shrink-0" size={15} />
                <div>
                  <p className="text-slate-400 font-medium">Scheduled Time</p>
                  <p className="font-bold text-slate-800">
                    {new Date(selectedEvent.scheduled_at).toLocaleString("en-US", {
                      weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
                    })}
                  </p>
                </div>
              </div>
            </div>

            {selectedEvent.google_meet_link && (
              <a
                href={selectedEvent.google_meet_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition"
              >
                <FiVideo size={15} /> Join Google Meet
              </a>
            )}

            <button
              onClick={() => setSelectedEvent(null)}
              className="w-full py-2.5 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
